/* eslint-disable object-curly-newline */

import Vue from 'vue';
import axios from 'axios';
import { zonedTimeToUtc } from 'date-fns-tz';

const state = {
  cards: null,
  draftCard: { // Is used in the selectCard action and in a getter, relevant for when new cards are added (all props need to be present to ensure reactivity)
    id: 0,
    type: 'card',
    title: null,
    relatedCollectionId: null,
    section: null,
    description: null,
    note: null,
    stars: 0,
    keywords: [],
    dateCreated: null,
    dateLastModified: null,
  },
};

const getters = {
  getCardRef: (state, getters, rootState) => ({ cardId, relatedCollectionId }) => {
    if (cardId === 0) return Object.assign({}, state.draftCard);
    if (!cardId) return false;

    // First try to get the card ref based on the specification (i.e. checking a particular collection).
    // If this fails or we do not have a specification, go through all collections to find the card ref.
    let requestedCard;

    // From a particular collection (first check if collection exists in rootstate.cards, might not be the case if the collection is new)
    const collectionExistsInCards = relatedCollectionId && !!rootState.cards.cards[relatedCollectionId];
    if (!requestedCard && relatedCollectionId && collectionExistsInCards) requestedCard = rootState.cards.cards[relatedCollectionId].find(card => card.id === cardId);

    // Check all collections
    if (!requestedCard) {
      Object.keys(state.cards).some((collectionId) => {
        requestedCard = state.cards[collectionId].find(card => card.id === cardId);
        return !!requestedCard;
      });
    }

    return requestedCard;
  },
  userId: (state, getters, rootState, rootGetters) => rootGetters['users/userId'],
  cardsByStars: (state) => {
    const stars = {
      1: [],
      2: [],
      3: [],
    };
    Object.values(state.cards).forEach((collection) => {
      collection.forEach((card) => {
        if (!card.stars) return;
        stars[card.stars].push(card);
      });
    });
    return stars;
  },
  cardsByKeyword: (state) => {
    const keywords = {};
    Object.values(state.cards).forEach((collection) => {
      collection.forEach((card) => {
        if (!card.keywords) return;
        card.keywords.forEach((keyword) => {
          if (keyword in keywords) keywords[keyword].push(card);
          else keywords[keyword] = [card];
        });
      });
    });
    return keywords;
  },
  keywords: (state, getters) => Object.keys(getters.cardsByKeyword).sort(),
};

const actions = {
  deleteAllCardsFromCollection({ state, dispatch, commit, rootState }, collectionId) {
    const cards = state.cards[collectionId];
    if (cards) {
      // Delete cards themselves and delete all history entries plus (if applicable) files
      commit('DeleteAllCardsFromCollection', collectionId);
      cards.forEach((card) => {
        commit('appState/DeleteFromHistory', `/card/${card.id}`, { root: true });
        commit('appState/CloseOpenItem', { id: card.id, type: 'card' }, { root: true });
        commit('users/UnpinItem', { id: card.id, type: 'card' }, { root: true });

        if (card.item && card.item.type === 'file') {
          const { id: cardId, relatedCollectionId, item } = card;
          dispatch('files/deleteFile', { cardId, relatedCollectionId, filename: item.address }, { root: true });
        }
      });

      dispatch('sync/enqueueUserUpdate', { settings: rootState.users.settings }, { root: true });
    }
  },
  modifyCards({ dispatch, commit, state, getters, rootState }, { cards = [], keywords = [] }) {
    cards.forEach((entry) => {
      const { action, id, data } = entry;
      if (!action || (!id && id !== 0) || (action === 'update' && !data)) {
        dispatch('notifications/setError', { error: 'Error: could not process an update request' }, { root: true });
        return;
      }

      const cardRef = getters.getCardRef({ cardId: id });
      const { relatedCollectionId } = cardRef;
      // DELETE ----------------------
      if (action === 'delete') {
        // Delete files if applicable
        if (cardRef.item && cardRef.item.type === 'file') {
          dispatch('files/deleteFile', { cardId: id, relatedCollectionId, filename: cardRef.item.address }, { root: true });
        }

        const deleteIndex = state.cards[relatedCollectionId].findIndex(card => card.id === id);
        commit('DeleteCard', { relatedCollectionId, deleteIndex });

        commit('appState/DeleteFromHistory', `/card/${id}`, { root: true });
        commit('appState/CloseOpenItem', { id, type: 'card' }, { root: true });
        commit('users/UnpinItem', { id, type: 'card' }, { root: true });
        dispatch('sync/enqueueUserUpdate', { settings: rootState.users.settings }, { root: true });
      }

      // UPDATE ----------------------
      else if (action === 'update') {
        // Check if we need to move the card to another collection
        if (id !== 0 && {}.hasOwnProperty.call(data, 'relatedCollectionId')) {
          const { relatedCollectionId: oldCollectionId, item } = cardRef;
          const oldIndex = state.cards[oldCollectionId] && state.cards[oldCollectionId].findIndex(card => card.id === id);
          commit('MoveCard', {
            cardRef,
            oldCollectionId,
            oldIndex,
            newCollectionId: data.relatedCollectionId,
          });

          // Moving a card means we should change the file key, if there's a file ofc
          if (item && item.type === 'file') {
            dispatch('files/modifyFileKey', {
              cardId: id,
              relatedCollectionId: oldCollectionId,
              filename: item.address,
              newCollectionId: data.relatedCollectionId,
            }, { root: true });
          }
        }

        Object.keys(data).forEach((field) => {
          commit('UpdateCard', { cardRef, field, data: data[field] });
        });
      }

      // If necessary, stringify data for the 'item' attribute (since it's a JSON column in the db)
      const enqueueObj = Object.assign({}, entry);
      if (enqueueObj.data && enqueueObj.data.item) enqueueObj.data.item = JSON.stringify(enqueueObj.data.item);
      dispatch('sync/enqueueCard', { cards: enqueueObj }, { root: true });
    });

    keywords.forEach((entry) => {
      const { action, id, keyword } = entry;
      if (!action || (!id && id !== 0) || !keyword) {
        dispatch('notifications/setError', { error: 'Error: could not process an update request' }, { root: true });
        return;
      }

      const cardRef = getters.getCardRef({ cardId: id });
      const data = cardRef.keywords ? cardRef.keywords.slice() : [];

      if (action === 'add') data.push(keyword);
      else if (action === 'delete') {
        const index = data.findIndex(kw => kw === keyword);
        data.splice(index, 1);
      }

      commit('UpdateCard', { cardRef, field: 'keywords', data });
      // Filter out new cards with id 0, they should not be enqueued
      if (entry.id) dispatch('sync/enqueueCard', { keywords: entry }, { root: true });
    });
  },
  getNewCardId({ dispatch, state, getters, rootState }, card) {
    return new Promise((resolve, reject) => {
      if (!card) {
        dispatch('notifications/setError', { error: 'Missing card!' });
      }
      // New cards have cardId 0
      const newCard = card;

      if (!newCard.title) newCard.title = 'draft';
      // Since newCard refers to the same object in use in the front-end to set the new card in Vuex later, the below attributes are also in the front-end.
      // Not super neat but it's okay.
      newCard.type = 'card';
      newCard.section = 'cards';
      newCard.dateLastModified = newCard.dateCreated;
      if (!newCard.relatedCollectionId || !newCard.dateCreated) {
        reject(new Error('Missing entries'));
        return;
      }

      axios.post(`cards/${getters.userId}`, newCard)
        .then((res) => {
          const { newCardId: cardId } = res.data;
          resolve(cardId);
        })
        .catch((err) => {
          if (err.message === 'Network Error') {
            const customMessage = 'Could not save changes due to a problem with the network. Please check your internet connection.';
            dispatch('notifications/setError', { error: err, customMessage }, { root: true });
          }
          else dispatch('notifications/setError', { error: err }, { root: true });
          reject();
        });
    });
  },
  getNewCardIds({ dispatch, commit, state, getters }, { number, relatedCollectionId }) {
    return new Promise(async (resolve, reject) => {
      const now = zonedTimeToUtc(new Date(), Intl.DateTimeFormat().resolvedOptions().timeZone).toISOString();
      const newCard = {
        title: 'draft',
        type: 'card',
        section: 'cards',
        relatedCollectionId,
        dateCreated: now,
        dateLastModified: now,
        description: null,
        relatedCards: [],
        stars: 0,
        keywords: [],
      };

      const promises = [];
      for (let i = 0; i < number; i++) {
        // Create promises
        const promise = new Promise(async (resolve, reject) => {
          const res = await axios.post(`cards/${getters.userId}`, newCard);
          resolve(res.data.newCardId);
        });
        promises.push(promise);
      }

      const cardIds = await Promise.all(promises);

      cardIds.forEach((id, index) => {
        const card = Object.assign({}, newCard, { id });
        commit('SetCard', card);
      });

      resolve(cardIds);
    });
  },
};

const mutations = {
  SetCards(state, payload) {
    state.cards = payload;
  },
  SetCard(state, card) {
    if (!state.cards[card.relatedCollectionId]) Vue.set(state.cards, card.relatedCollectionId, []);
    state.cards[card.relatedCollectionId].unshift(card);
  },
  DeleteAllCardsFromCollection(state, collectionId) {
    Vue.delete(state.cards, collectionId);
  },
  UpdateCard(state, { cardRef, field, data }) {
    Vue.set(cardRef, field, data);
    cardRef.dateLastModified = zonedTimeToUtc(new Date(), Intl.DateTimeFormat().resolvedOptions().timeZone).toISOString(); // why is this here?
  },
  MoveCard(state, { cardRef, oldCollectionId, oldIndex, newCollectionId }) {
    state.cards[oldCollectionId].splice(oldIndex, 1);
    if (!state.cards[newCollectionId]) state.cards[newCollectionId] = [];
    state.cards[newCollectionId].unshift(cardRef);
  },
  DeleteCard(state, { relatedCollectionId, deleteIndex }) {
    Vue.delete(state.cards[relatedCollectionId], deleteIndex);
  },
  ResetState(state) {
    state.cards = null;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
